import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { sharedMessages } from '../../utils/uiMessages';

const CUSTOM_OPTION = '__custom__';
const EXCLUDED_JOB_KIND_OPTIONS = new Set([
  'Reporting to the client',
  'Prepare submission to the PTO',
  'Others',
]);
const BASE_PRESET_VALUES = [
  'Application drafting',
  'OA reporting',
  'Prepare response to the PTO',
  'Client communication',
  'File response with the PTO',
  'Research / analysis',
];

const messages = defineMessages({
  chooseJobTitle: {
    id: 'Choose a job title',
    defaultMessage: 'Choose a job title',
  },
  customJobTitle: {
    id: 'Custom job title',
    defaultMessage: 'Custom job title',
  },
  jobTitleHelp: {
    id: 'Choose a standard job title or enter your own.',
    defaultMessage: 'Choose a standard job title or enter your own.',
  },
  useFreeText: {
    id: 'Use free text',
    defaultMessage: 'Use free text',
  },
});

function normalizePresetValues(values) {
  const normalized = [];
  const seen = new Set();

  for (const value of values || []) {
    const label = String(value || '').trim();
    if (!label) {
      continue;
    }
    const key = label.toLocaleLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    normalized.push(label);
  }

  return normalized;
}

function deriveMode(value, options) {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return '';
  }
  return options.includes(trimmed) ? trimmed : CUSTOM_OPTION;
}

export default function JobTitleField({
  value,
  onChange,
  required = false,
  options = [],
}) {
  const intl = useIntl();
  const presetValues = useMemo(
    () =>
      normalizePresetValues([
        ...BASE_PRESET_VALUES,
        ...(options || []).filter(
          (option) => !EXCLUDED_JOB_KIND_OPTIONS.has(String(option || '').trim()),
        ),
      ]),
    [options],
  );
  const [mode, setMode] = useState(() => deriveMode(value, presetValues));

  useEffect(() => {
    setMode(deriveMode(value, presetValues));
  }, [presetValues, value]);

  return (
    <>
      <label className="patent-entry__field">
        <span className="patent-entry__label">
          {intl.formatMessage(sharedMessages.jobTitle)}
        </span>
        <select
          className="patent-entry__input"
          value={mode}
          onChange={(event) => {
            const nextMode = event.target.value;
            setMode(nextMode);
            if (presetValues.includes(nextMode)) {
              onChange(nextMode);
              return;
            }
            if (nextMode === CUSTOM_OPTION) {
              if (presetValues.includes(String(value || '').trim())) {
                onChange('');
              }
              return;
            }
            onChange('');
          }}
          required={required && !String(value || '').trim()}
        >
          <option value="">
            {intl.formatMessage(messages.chooseJobTitle)}
          </option>
          {presetValues.map((option) => (
            <option key={option} value={option}>
              {intl.formatMessage({
                id: option,
                defaultMessage: option,
              })}
            </option>
          ))}
          <option value={CUSTOM_OPTION}>
            {intl.formatMessage(messages.useFreeText)}
          </option>
        </select>
        <span className="patent-entry__help">
          {intl.formatMessage(messages.jobTitleHelp)}
        </span>
      </label>

      {mode === CUSTOM_OPTION ? (
        <label className="patent-entry__field">
          <span className="patent-entry__label">
            {intl.formatMessage(messages.customJobTitle)}
          </span>
          <input
            className="patent-entry__input"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            required={required}
          />
        </label>
      ) : null}
    </>
  );
}
